/* LSA Photo Gallery · filters, show-more, counter, mobile orphans, lightbox.
   See the comment at the top of sections/lsa-photo-gallery.liquid. */
if (!customElements.get('lsa-photo-gallery')) {
  customElements.define(
    'lsa-photo-gallery',
    class LsaPhotoGallery extends HTMLElement {
      connectedCallback() {
        this.frames = Array.from(this.querySelectorAll('[data-lsa-pg-frame]'));
        this.filters = Array.from(this.querySelectorAll('[data-lsa-pg-filter]'));
        this.more = this.querySelector('[data-lsa-pg-more]');
        this.shownEl = this.querySelector('[data-lsa-pg-shown]');
        this.totalEl = this.querySelector('[data-lsa-pg-total]');
        this.segment = this.querySelector('[data-lsa-pg-segment]');
        this.lightbox = this.querySelector('[data-lsa-pg-lightbox]');
        this.step = Number(this.dataset.initial) || 8;
        this.limit = this.step;
        this.category = '';
        this.mobileMQ = window.matchMedia('(max-width: 749px)');

        this.filters.forEach((btn) =>
          btn.addEventListener('click', () => {
            this.category = btn.dataset.lsaPgFilter || '';
            this.limit = this.step;
            this.filters.forEach((b) => {
              const on = b === btn;
              b.classList.toggle('is-active', on);
              b.setAttribute('aria-pressed', on ? 'true' : 'false');
            });
            this.render();
          })
        );

        if (this.more) {
          this.more.addEventListener('click', () => {
            const firstNew = this.matching()[this.limit];
            this.limit += this.step;
            this.render();
            // Move focus to the first newly revealed frame for keyboard users.
            const target = firstNew && firstNew.querySelector('[data-lsa-pg-open]');
            if (target) target.focus({ preventScroll: true });
          });
        }

        this.onMQ = () => this.markOrphans();
        this.mobileMQ.addEventListener('change', this.onMQ);

        if (this.lightbox && this.dataset.lightbox === 'true') this.setupLightbox();
        this.render();
      }

      disconnectedCallback() {
        this.mobileMQ.removeEventListener('change', this.onMQ);
        if (this.onKey) document.removeEventListener('keydown', this.onKey);
        document.body.classList.remove('overflow-hidden');
        // The lightbox lives on <body>; take it with us when the theme editor
        // re-renders or removes the section, or copies pile up.
        if (this.lightbox && this.lightbox.parentNode === document.body) this.lightbox.remove();
      }

      matching() {
        return this.frames.filter((f) => !this.category || f.dataset.category === this.category);
      }

      render() {
        const matching = this.matching();
        this.frames.forEach((f) => (f.hidden = true));
        matching.forEach((f, i) => (f.hidden = i >= this.limit));
        const shown = Math.min(this.limit, matching.length);

        if (this.more) this.more.hidden = shown >= matching.length;
        const pad = (n) => String(n).padStart(2, '0');
        if (this.shownEl) this.shownEl.textContent = pad(shown);
        if (this.totalEl) this.totalEl.textContent = pad(matching.length);
        if (this.segment) this.segment.style.width = matching.length ? `${(shown / matching.length) * 100}%` : '0';
        this.markOrphans();
      }

      // Mobile: a one-column frame with no partner in its row spans both.
      markOrphans() {
        this.frames.forEach((f) => f.classList.remove('is-orphan'));
        if (!this.mobileMQ.matches) return;
        let run = [];
        const close = () => {
          if (run.length % 2 === 1) run[run.length - 1].classList.add('is-orphan');
          run = [];
        };
        this.frames
          .filter((f) => !f.hidden)
          .forEach((f) => {
            if (f.classList.contains('lsa-pg__frame--large')) close();
            else run.push(f);
          });
        close();
      }

      /* ---------------- Lightbox ---------------- */
      setupLightbox() {
        const lb = this.lightbox;
        // Move the dialog to <body> so no ancestor transform or overflow clips it.
        document.body.appendChild(lb);
        // Outside the section it no longer inherits the section's variables,
        // so carry the resolved values across.
        const cs = getComputedStyle(this);
        [
          '--lsa-pg-pad-x-mobile',
          '--lsa-pg-pad-x-desktop',
          '--lsa-pg-accent',
          '--lsa-pg-hairline',
          '--lsa-pg-color-body',
          '--lsa-pg-color-heading',
          '--lsa-pg-color-italic',
          '--lsa-pg-color-cta',
          '--lsa-pg-color-hairline',
        ].forEach((name) => {
          const value = cs.getPropertyValue(name).trim();
          if (value) lb.style.setProperty(name, value);
        });

        this.lbImg = lb.querySelector('[data-lb-img]');
        this.lbName = lb.querySelector('[data-lb-name]');
        this.lbMark = lb.querySelector('[data-lb-mark]');
        this.lbCaption = lb.querySelector('[data-lb-caption]');
        this.lbLink = lb.querySelector('[data-lb-link]');
        this.lbCurrent = lb.querySelector('[data-lb-current]');
        this.lbTotal = lb.querySelector('[data-lb-total]');
        this.lbStrip = lb.querySelector('[data-lb-strip]');

        this.frames.forEach((f) => {
          const btn = f.querySelector('[data-lsa-pg-open]');
          if (btn) btn.addEventListener('click', () => this.openLightbox(f));
        });
        lb.querySelector('[data-lb-close]').addEventListener('click', () => this.closeLightbox());
        lb.querySelector('[data-lb-prev]').addEventListener('click', () => this.go(-1));
        lb.querySelector('[data-lb-next]').addEventListener('click', () => this.go(1));

        this.onKey = (e) => {
          if (lb.hidden) return;
          if (e.key === 'Escape') this.closeLightbox();
          else if (e.key === 'ArrowLeft') this.go(-1);
          else if (e.key === 'ArrowRight') this.go(1);
          else if (e.key === 'Tab') this.trapFocus(e);
        };
        document.addEventListener('keydown', this.onKey);

        let startX = null;
        lb.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
        lb.addEventListener(
          'touchend',
          (e) => {
            if (startX === null) return;
            const dx = e.changedTouches[0].clientX - startX;
            if (Math.abs(dx) > 40) this.go(dx < 0 ? 1 : -1);
            startX = null;
          },
          { passive: true }
        );
      }

      detail(frame) {
        const tpl = frame.querySelector('template[data-lsa-pg-detail]');
        const read = (k) => (tpl ? (tpl.content.querySelector(`[data-${k}]`)?.textContent || '').trim() : '');
        const img = frame.querySelector('.lsa-pg__img');
        return {
          name: read('name'),
          mark: read('mark'),
          caption: read('caption'),
          link: read('link'),
          src: img ? img.dataset.full || img.currentSrc || img.src : '',
          thumb: img ? img.currentSrc || img.src : '',
          alt: img ? img.alt : '',
        };
      }

      openLightbox(frame) {
        this.set = this.matching();
        this.index = Math.max(0, this.set.indexOf(frame));
        this.returnFocus = frame.querySelector('[data-lsa-pg-open]');
        this.buildStrip();
        this.show();
        this.lightbox.hidden = false;
        document.body.classList.add('overflow-hidden');
        requestAnimationFrame(() => this.lightbox.classList.add('is-open'));
        this.lightbox.focus();
      }

      closeLightbox() {
        this.lightbox.classList.remove('is-open');
        this.lightbox.hidden = true;
        document.body.classList.remove('overflow-hidden');
        if (this.returnFocus) this.returnFocus.focus();
      }

      go(delta) {
        if (!this.set || !this.set.length) return;
        this.index = (this.index + delta + this.set.length) % this.set.length;
        this.show();
      }

      show() {
        const d = this.detail(this.set[this.index]);
        const pad = (n) => String(n).padStart(2, '0');
        this.lbImg.src = d.src;
        this.lbImg.alt = d.alt;
        this.lbName.textContent = d.name;
        this.lbMark.textContent = d.mark;
        this.lbCaption.textContent = d.caption;
        if (d.link) {
          this.lbLink.href = d.link;
          this.lbLink.hidden = false;
        } else {
          this.lbLink.hidden = true;
        }
        this.lbCurrent.textContent = pad(this.index + 1);
        this.lbTotal.textContent = pad(this.set.length);
        Array.from(this.lbStrip.children).forEach((t, i) => {
          const on = i === this.index;
          t.setAttribute('aria-current', on ? 'true' : 'false');
          if (on) t.scrollIntoView({ block: 'nearest', inline: 'center' });
        });
      }

      buildStrip() {
        this.lbStrip.textContent = '';
        this.set.forEach((frame, i) => {
          const d = this.detail(frame);
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'lsa-pg__lb-thumb';
          b.setAttribute('aria-label', d.name || d.mark);
          if (d.thumb) {
            const im = document.createElement('img');
            im.src = d.thumb;
            im.alt = '';
            im.loading = 'lazy';
            b.appendChild(im);
          }
          b.addEventListener('click', () => {
            this.index = i;
            this.show();
          });
          this.lbStrip.appendChild(b);
        });
      }

      trapFocus(e) {
        const items = Array.from(
          this.lightbox.querySelectorAll('button:not([hidden]), a[href]:not([hidden])')
        ).filter((el) => el.offsetParent !== null);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  );
}
